import { useState, useRef, useEffect, type MouseEvent as ReactMouseEvent } from 'react';
import { X, Send, User, Loader2, Info, Database, Trash2, Search, History, Maximize2, Minimize2, Cpu, Download, FileText, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import AIIcon from './AIIcon';
import { aiApi } from '@/api/ai';
import type { AIChatMessage, AIChartSpec } from '@/api/ai';
import DropdownMenu, { type DropdownItem } from '@/ui/common/DropdownMenu';
import { fetchUsers, type UserListItem } from '@/api/users';
import { useAuthStore } from '@/store/authStore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import { toPng } from 'html-to-image';
import html2canvas from 'html2canvas';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { confirmToast } from '@/lib/notify';

interface Message extends AIChatMessage {
  id: string;
}

interface PdfPreviewState {
  url: string;
  filename: string;
  title: string;
  messageId: string;
  prompt?: string;
  vendor?: string;
  model?: string;
}

interface AttachmentMailDraft {
  title: string;
  filename: string;
  contentType: string;
  blob: Blob;
}

interface AIImageAttachment {
  previewUrl: string;
  dataBase64: string;
  mimeType: string;
  width: number;
  height: number;
}

interface SelectionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface AIChatWindowProps {
  isOpen: boolean;
  onClose: () => void;
  command?: { text: string; id: number } | null;
  initialVendor?: 'gemini' | 'anthropic' | 'deepseek';
  initialGeminiVersion?: '2.5' | '3';
  captureRequestId?: number;
}

const CHART_COLORS = ['#2563eb', '#0ea5e9', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6'];
const VENDOR_LABELS: Record<'gemini' | 'anthropic' | 'deepseek', string> = {
  gemini: 'Gemini',
  anthropic: 'Claude',
  deepseek: 'DeepSeek',
};
const ALL_VENDORS: Array<'gemini' | 'anthropic' | 'deepseek'> = ['gemini', 'anthropic', 'deepseek'];

async function blobToBase64(blob: Blob): Promise<string> {
  const arrayBuffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

function formatStatValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '0';
  if (typeof value === 'number') return Number.isFinite(value) ? value.toLocaleString('tr-TR') : String(value);
  return String(value);
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function getUsageStats(usage: any) {
  if (!usage || typeof usage !== 'object') return null;

  const inputTokens =
    toFiniteNumber(usage.promptTokenCount) ??
    toFiniteNumber(usage.inputTokenCount) ??
    toFiniteNumber(usage.input_tokens) ??
    toFiniteNumber(usage.prompt_tokens);

  const outputTokens =
    toFiniteNumber(usage.candidatesTokenCount) ??
    toFiniteNumber(usage.outputTokenCount) ??
    toFiniteNumber(usage.output_tokens) ??
    toFiniteNumber(usage.completion_tokens);

  const totalTokens =
    toFiniteNumber(usage.totalTokens) ??
    toFiniteNumber(usage.total_tokens) ??
    ((inputTokens ?? null) !== null || (outputTokens ?? null) !== null
      ? (inputTokens ?? 0) + (outputTokens ?? 0)
      : null);

  const cachedTokens =
    toFiniteNumber(usage.cachedContentTokenCount) ??
    toFiniteNumber(usage.cacheReadInputTokens) ??
    toFiniteNumber(usage.cache_read_input_tokens);

  const thoughtTokens =
    toFiniteNumber(usage.thoughtsTokenCount) ??
    toFiniteNumber(usage.reasoningTokenCount) ??
    toFiniteNumber(usage.reasoning_tokens);

  return {
    inputTokens,
    outputTokens,
    totalTokens,
    cachedTokens,
    thoughtTokens,
  };
}

function getAnalysisStats(meta: any) {
  const usageStats = getUsageStats(meta?.usage);
  const latencyMs = toFiniteNumber(meta?.latencyMs);
  const historyCount = meta?.context?.historyUsed?.length ?? null;
  const memoryCount = meta?.context?.memoriesUsed?.length ?? null;
  const sqlCount = meta?.context?.sqlQueries?.length ?? null;
  const toolCount = meta?.context?.toolCalls?.length ?? meta?.toolCalls?.length ?? null;
  const hasChart = meta?.chart ? 1 : null;
  const tokenPerSecond =
    usageStats?.totalTokens && latencyMs && latencyMs > 0
      ? Math.round((usageStats.totalTokens / latencyMs) * 1000)
      : null;

  return [
    { label: 'Input Token', value: usageStats?.inputTokens },
    { label: 'Output Token', value: usageStats?.outputTokens },
    { label: 'Toplam Token', value: usageStats?.totalTokens },
    { label: 'Cached Token', value: usageStats?.cachedTokens },
    { label: 'Reasoning Token', value: usageStats?.thoughtTokens },
    { label: 'Token / sn', value: tokenPerSecond },
    { label: 'History', value: historyCount, suffix: 'mesaj' },
    { label: 'Memory', value: memoryCount, suffix: 'kayıt' },
    { label: 'SQL', value: sqlCount, suffix: 'sorgu' },
    { label: 'Tool', value: toolCount, suffix: 'çağrı' },
    { label: 'Chart', value: hasChart, suffix: 'adet' },
  ].filter((item) => item.value !== null && item.value !== undefined);
}

function AIPdfModal({
  preview,
  users,
  currentUserEmail,
  onClose,
}: {
  preview: PdfPreviewState;
  users: UserListItem[];
  currentUserEmail?: string | null;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [selectedEmail, setSelectedEmail] = useState(currentUserEmail ?? '');
  const [mailSending, setMailSending] = useState(false);

  const activeUsers = users.filter((user) => user.is_active === 1 && user.email?.trim());

  useEffect(() => {
    setSelectedEmail(currentUserEmail ?? '');
  }, [currentUserEmail, preview.url]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = preview.url;
    link.download = preview.filename;
    link.click();
  };

  const handleSendMail = async () => {
    if (!selectedEmail) return;

    setMailSending(true);
    try {
      const response = await fetch(preview.url);
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      const chunkSize = 0x8000;
      for (let index = 0; index < bytes.length; index += chunkSize) {
        const chunk = bytes.subarray(index, index + chunkSize);
        binary += String.fromCharCode(...chunk);
      }
      const contentBase64 = btoa(binary);

      await aiApi.sendPdfByEmail({
        email: selectedEmail,
        filename: preview.filename,
        title: preview.title,
        contentBase64,
        prompt: preview.prompt,
        vendor: preview.vendor,
        model: preview.model,
      });

      toast.success('AI raporu mail kuyruğuna alındı.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'AI raporu maili gönderilemedi.';
      toast.error(message);
    } finally {
      setMailSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        className="flex h-[90vh] w-[90vw] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex flex-shrink-0 items-center justify-between gap-4 border-b bg-gray-50 px-5 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <FileText className="h-4 w-4 text-rose-600" />
            <span className="truncate text-sm font-semibold text-gray-800">{preview.title}</span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <select
              value={selectedEmail}
              onChange={(event) => setSelectedEmail(event.target.value)}
              className="h-9 min-w-[240px] rounded-lg border border-amber-200 bg-white px-3 text-xs text-gray-700 outline-none transition focus:border-amber-400"
            >
              <option value="">E-posta seç</option>
              {activeUsers.map((user) => (
                <option key={user.user_uuid_id} value={user.email}>
                  {(user.fullname || user.username) + ' - ' + user.email}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleSendMail}
              disabled={!selectedEmail || mailSending}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 text-xs font-medium text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mailSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
              Gönder
            </button>
            <button
              type="button"
              onClick={handleDownload}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-100"
            >
              <Download className="h-3.5 w-3.5" /> İndir
            </button>
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <iframe src={preview.url} className="flex-1 w-full" title={preview.title} />
      </div>
    </div>
  );
}

function AIAttachmentMailModal({
  draft,
  users,
  currentUserEmail,
  onClose,
}: {
  draft: AttachmentMailDraft;
  users: UserListItem[];
  currentUserEmail?: string | null;
  onClose: () => void;
}) {
  const [selectedEmail, setSelectedEmail] = useState(currentUserEmail ?? '');
  const [mailSending, setMailSending] = useState(false);
  const activeUsers = users.filter((user) => user.is_active === 1 && user.email?.trim());

  useEffect(() => {
    setSelectedEmail(currentUserEmail ?? '');
  }, [currentUserEmail, draft.filename]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSendMail = async () => {
    if (!selectedEmail) return;
    setMailSending(true);
    try {
      const contentBase64 = await blobToBase64(draft.blob);
      await aiApi.sendAttachmentByEmail({
        email: selectedEmail,
        filename: draft.filename,
        title: draft.title,
        contentBase64,
        contentType: draft.contentType,
      });
      toast.success('Ek mail kuyruğuna alındı.');
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Ek maili gönderilemedi.';
      toast.error(message);
    } finally {
      setMailSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[125] flex items-center justify-center bg-black/70" onClick={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="w-[520px] max-w-[92vw] rounded-2xl bg-white p-5 shadow-2xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800">Mail ile Gönder</div>
            <div className="mt-1 truncate text-xs text-gray-500">{draft.filename}</div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3">
          <select
            value={selectedEmail}
            onChange={(event) => setSelectedEmail(event.target.value)}
            className="h-10 w-full rounded-lg border border-amber-200 bg-white px-3 text-xs text-gray-700 outline-none transition focus:border-amber-400"
          >
            <option value="">E-posta seç</option>
            {activeUsers.map((item) => (
              <option key={item.user_uuid_id} value={item.email}>
                {(item.fullname || item.username) + ' - ' + item.email}
              </option>
            ))}
          </select>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="h-9 rounded-lg border border-gray-200 px-3 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              İptal
            </button>
            <button
              type="button"
              onClick={handleSendMail}
              disabled={!selectedEmail || mailSending}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 text-xs font-medium text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mailSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
              Gönder
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function getModelBadgeLabel(vendor?: string, model?: string) {
  if (vendor === 'gemini') {
    if (model?.includes('gemini-3')) return 'Gemini • v3';
    return 'Gemini • v2.5';
  }
  if (vendor === 'anthropic') return 'Claude';
  if (vendor === 'deepseek') return 'DeepSeek';
  return model || vendor || 'Model';
}

function AIChartCard({
  chart,
  onQueueAttachmentMail,
}: {
  chart: AIChartSpec;
  onQueueAttachmentMail: (draft: AttachmentMailDraft) => void;
}) {
  const chartRef = useRef<HTMLDivElement>(null);

  const buildCsvBlob = () => {
    if (!chart.data || chart.data.length === 0) return;
    const headers = Object.keys(chart.data[0]);
    let csv = [headers.join(',')];
    chart.data.forEach(row => {
      csv.push(headers.map(h => `"${String(row[h] ?? '').replace(/"/g, '""')}"`).join(','));
    });
    return new Blob([csv.join('\n')], { type: 'text/csv;charset=utf-8;' });
  };

  const downloadCSV = () => {
    const csvFile = buildCsvBlob();
    if (!csvFile) return;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(csvFile);
    link.setAttribute('download', `${chart.title.replace(/\s+/g, '_').toLowerCase()}_verisi_${Date.now()}.csv`);
    link.click();
    toast.success('Veri CSV olarak indirildi');
  };

  const buildImageBlob = async () => {
    if (!chartRef.current) return;
    const dataUrl = await toPng(chartRef.current, {
      backgroundColor: '#ffffff',
      quality: 1,
      pixelRatio: 2,
      filter: (node: HTMLElement) => !node.classList?.contains('export-ignore')
    });
    const imageResponse = await fetch(dataUrl);
    return imageResponse.blob();
  };

  const downloadImage = async () => {
    try {
      const imageBlob = await buildImageBlob();
      if (!imageBlob) return;
      const link = document.createElement('a');
      link.download = `${chart.title.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}.png`;
      link.href = URL.createObjectURL(imageBlob);
      link.click();
      toast.success('Grafik resmi indirildi');
    } catch {
      toast.error('Resim oluşturulamadı');
    }
  };

  const mailChartCsv = () => {
    const csvBlob = buildCsvBlob();
    if (!csvBlob) return;
    onQueueAttachmentMail({
      title: `${chart.title} - Veri`,
      filename: `${chart.title.replace(/\s+/g, '_').toLowerCase()}_veri.csv`,
      contentType: 'text/csv',
      blob: csvBlob,
    });
  };

  const mailChartImage = async () => {
    try {
      const imageBlob = await buildImageBlob();
      if (!imageBlob) return;
      onQueueAttachmentMail({
        title: `${chart.title} - Gorsel`,
        filename: `${chart.title.replace(/\s+/g, '_').toLowerCase()}.png`,
        contentType: 'image/png',
        blob: imageBlob,
      });
    } catch {
      toast.error('Resim hazırlanamadı');
    }
  };

  const series = chart.series?.length
    ? chart.series
    : (chart.yKeys ?? []).map((key, index) => ({
        key,
        label: key,
        color: CHART_COLORS[index % CHART_COLORS.length],
      }));

  const pieKey = chart.series?.[0]?.key;
  const pieLabelKey = chart.labelKey;
  const isValid = chart.type === 'pie' ? (pieKey && pieLabelKey) : (chart.xKey && chart.yKeys?.length);

  if (!isValid) return null;

  const chartActionItems: DropdownItem[] = [
    {
      label: 'Veriyi CSV İndir',
      icon: <Database size={14} />,
      onClick: downloadCSV,
      variant: 'info',
    },
    {
      label: 'Resim İndir (PNG)',
      icon: <Download size={14} />,
      onClick: () => {
        void downloadImage();
      },
      variant: 'success',
    },
    {
      label: 'CSV Mail Gönder',
      icon: <Mail size={14} />,
      onClick: mailChartCsv,
      variant: 'warning',
    },
    {
      label: 'Resim Mail Gönder',
      icon: <Mail size={14} />,
      onClick: () => {
        void mailChartImage();
      },
      variant: 'info',
    },
  ];

  return (
    <div ref={chartRef} className="group mt-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm w-full overflow-hidden relative">
      <div className="export-ignore absolute right-4 top-4 z-20 opacity-0 group-hover:opacity-100 transition-all">
        <DropdownMenu title="İşlemler" items={chartActionItems} className="!inline-block" />
      </div>
      <div className="mb-3 pr-16">
        <h4 className="text-[12px] font-bold text-slate-800 tracking-tight">{chart.title}</h4>
        {chart.description ? <p className="mt-1 text-[9px] text-slate-500 leading-tight">{chart.description}</p> : null}
      </div>
      <div className="h-56 w-full relative min-h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          {chart.type === 'pie' && pieKey && pieLabelKey ? (
            <PieChart>
              <Tooltip labelStyle={{ fontSize: '10px' }} contentStyle={{ borderRadius: '8px', fontSize: '10px' }} />
              <Legend verticalAlign="bottom" height={36} iconSize={8} wrapperStyle={{ fontSize: '9px' }} />
              <Pie data={chart.data} dataKey={pieKey} nameKey={pieLabelKey} innerRadius={40} outerRadius={65} paddingAngle={4} animationDuration={1000}>
                {chart.data.map((_, index) => <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
              </Pie>
            </PieChart>
          ) : chart.type === 'line' ? (
            <LineChart data={chart.data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey={chart.xKey} tick={{ fontSize: 9, fontWeight: 500 }} stroke="#94a3b8" axisLine={false} tickLine={false} dy={10} />
              <YAxis tick={{ fontSize: 9, fontWeight: 500 }} stroke="#94a3b8" axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '10px' }} />
              <Legend verticalAlign="top" height={36} align="right" iconSize={8} wrapperStyle={{ fontSize: '9px' }} />
              {series.map((item, index) => (
                <Line key={item.key} type="monotone" dataKey={item.key} name={item.label} stroke={item.color ?? CHART_COLORS[index % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3, fill: item.color ?? CHART_COLORS[index % CHART_COLORS.length], stroke: '#fff' }} animationDuration={1000} />
              ))}
            </LineChart>
          ) : chart.type === 'area' ? (
            <AreaChart data={chart.data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <defs>
                {series.map((item, index) => (
                  <linearGradient key={`grad-${item.key}`} id={`grad-${item.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={item.color ?? CHART_COLORS[index % CHART_COLORS.length]} stopOpacity={0.2}/><stop offset="95%" stopColor={item.color ?? CHART_COLORS[index % CHART_COLORS.length]} stopOpacity={0}/>
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey={chart.xKey} tick={{ fontSize: 9, fontWeight: 500 }} stroke="#94a3b8" axisLine={false} tickLine={false} dy={10} />
              <YAxis tick={{ fontSize: 9, fontWeight: 500 }} stroke="#94a3b8" axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '10px' }} />
              <Legend verticalAlign="top" height={36} align="right" iconSize={8} wrapperStyle={{ fontSize: '9px' }} />
              {series.map((item, index) => (
                <Area key={item.key} type="monotone" dataKey={item.key} name={item.label} stroke={item.color ?? CHART_COLORS[index % CHART_COLORS.length]} fill={`url(#grad-${item.key})`} strokeWidth={2} animationDuration={1000} />
              ))}
            </AreaChart>
          ) : (
            <BarChart data={chart.data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey={chart.xKey} tick={{ fontSize: 9, fontWeight: 500 }} stroke="#94a3b8" axisLine={false} tickLine={false} dy={10} />
              <YAxis tick={{ fontSize: 9, fontWeight: 500 }} stroke="#94a3b8" axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '10px' }} />
              <Legend verticalAlign="top" height={36} align="right" iconSize={8} wrapperStyle={{ fontSize: '9px' }} />
              {series.map((item, index) => (
                <Bar key={item.key} dataKey={item.key} name={item.label} fill={item.color ?? CHART_COLORS[index % CHART_COLORS.length]} radius={[4, 4, 0, 0]} barSize={24} animationDuration={1000} />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function AIChatWindow({ isOpen, onClose, command, initialVendor, initialGeminiVersion, captureRequestId }: AIChatWindowProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [vendor, setVendor] = useState<'gemini' | 'anthropic' | 'deepseek'>(initialVendor || 'gemini');
  const [availableVendors, setAvailableVendors] = useState<Array<'gemini' | 'anthropic' | 'deepseek'>>(['gemini', 'anthropic', 'deepseek']);
  const [geminiVersion, setGeminiVersion] = useState<'2.5' | '3'>(initialGeminiVersion || '3');
  const [selectedMeta, setSelectedMeta] = useState<any | null>(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Message[]>([]);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [pdfPreview, setPdfPreview] = useState<PdfPreviewState | null>(null);
  const [attachmentMailDraft, setAttachmentMailDraft] = useState<AttachmentMailDraft | null>(null);
  const [imageAttachment, setImageAttachment] = useState<AIImageAttachment | null>(null);
  const [isCaptureMode, setIsCaptureMode] = useState(false);
  const [isCapturingSnapshot, setIsCapturingSnapshot] = useState(false);
  const [captureStartPoint, setCaptureStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [selectionRect, setSelectionRect] = useState<SelectionRect | null>(null);
  
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastProcessedId = useRef<number | null>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const user = useAuthStore((state) => state.user);
  const previousBodyOverflow = useRef<string>('');
  const lastProcessedCaptureId = useRef<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadConfig();
      loadHistory();
      loadUsers();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, vendor]);

  useEffect(() => {
    return () => {
      if (pdfPreview) URL.revokeObjectURL(pdfPreview.url);
    };
  }, [pdfPreview]);

  useEffect(() => {
    return () => {
      if (imageAttachment?.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(imageAttachment.previewUrl);
      }
    };
  }, [imageAttachment]);

  useEffect(() => {
    if (initialVendor) setVendor(initialVendor);
  }, [initialVendor]);

  useEffect(() => {
    if (initialGeminiVersion) setGeminiVersion(initialGeminiVersion);
  }, [initialGeminiVersion]);

  useEffect(() => {
    if (command && isOpen && lastProcessedId.current !== command.id) {
      handleSend(command.text);
      lastProcessedId.current = command.id;
    }
  }, [command, isOpen]);

  useEffect(() => {
    if (!isOpen || !captureRequestId) return;
    if (lastProcessedCaptureId.current === captureRequestId) return;
    lastProcessedCaptureId.current = captureRequestId;
    setTimeout(() => beginScreenCapture(), 120);
  }, [captureRequestId, isOpen]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isCaptureMode) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        cancelCaptureMode();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isCaptureMode]);

  useEffect(() => {
    if (isCaptureMode) {
      previousBodyOverflow.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return;
    }
    document.body.style.overflow = previousBodyOverflow.current;
  }, [isCaptureMode]);

  const loadConfig = async () => {
    try {
      const config = await aiApi.getAdminConfig();
      const activeVendors = ALL_VENDORS.filter((candidate) =>
        (config.models || []).some((model) => model.vendor === candidate && model.is_active),
      );

      const nextAvailable: Array<'gemini' | 'anthropic' | 'deepseek'> = activeVendors.length ? activeVendors : ['gemini'];
      setAvailableVendors(nextAvailable);
      if (!nextAvailable.includes(vendor)) {
        setVendor(nextAvailable[0]);
      }
    } catch (error) {
      console.error('AI config yüklenemedi:', error);
    }
  };

  const loadHistory = async () => {
    try {
      const history = await aiApi.getHistory();
      setMessages(history.map((m, i) => ({ 
        ...m, 
        id: m.id || `hist-${i}-${Date.now()}` 
      })));
    } catch (error) {
      console.error('Hafıza yüklenemedi:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await fetchUsers();
      setUsers(data);
    } catch (error) {
      console.error('Kullanıcı listesi yüklenemedi:', error);
    }
  };

  const handleClear = async () => {
    if (!(await confirmToast('Sohbet geçmişi temizlensin mi?', 'Tüm sohbetler silinecek.'))) return;
    try {
      await aiApi.clearHistory();
      setMessages([]);
      setIsSearchMode(false);
      toast.success('Sohbet geçmişi temizlendi');
    } catch {
      toast.error('Sohbet geçmişi silinemedi');
    }
  };

  const handleSearch = async (q: string) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults([]); return; }
    try {
      const results = await aiApi.searchHistory(q);
      setSearchResults(results.map((m, i) => ({ ...m, id: `search-${i}` })));
    } catch (error: any) {
      console.error('Arama hatası:', error);
    }
  };

  const buildPdfPreview = async (messageId: string) => {
    const el = messageRefs.current.get(messageId);
    if (!el) throw new Error('PDF kaynağı bulunamadı.');

    const message = messages.find((item) => item.id === messageId);
    const { jsPDF } = await import('jspdf');
    const dataUrl = await toPng(el, {
      backgroundColor: '#ffffff',
      pixelRatio: 2,
      filter: (node: HTMLElement) => !node.classList?.contains('export-ignore'),
    });
    const img = new Image();
    img.src = dataUrl;
    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
    });

    const imgW = img.naturalWidth;
    const imgH = img.naturalHeight;
    const pdfW = 210;
    const margin = 15;
    const headerH = 14;
    const footerH = 12;
    const contentW = pdfW - margin * 2;
    const scaleFactor = contentW / (imgW / 2);
    const scaledH = (imgH / 2) * scaleFactor;
    const pageH = 297;
    const usableH = pageH - headerH - footerH - margin;
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    const addHeader = () => {
      pdf.setFillColor(37, 99, 235);
      pdf.rect(0, 0, pdfW, headerH, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.text('GES MASTER AI RAPOR SİSTEMİ', margin, 9);
      pdf.text(
        new Date().toLocaleDateString('tr-TR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        pdfW - margin,
        9,
        { align: 'right' },
      );
    };

    addHeader();
    let srcY = 0;
    let remaining = scaledH;
    let isFirst = true;

    while (remaining > 0) {
      if (!isFirst) {
        pdf.addPage();
        addHeader();
      }
      const sliceMm = Math.min(remaining, usableH);
      const slicePx = Math.ceil((sliceMm / scaleFactor) * 2);
      const canvas = document.createElement('canvas');
      canvas.width = imgW;
      canvas.height = slicePx;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('PDF görüntüsü hazırlanamadı.');
      }
      ctx.drawImage(img, 0, -srcY, imgW, imgH);
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, headerH + 2, contentW, sliceMm);
      srcY += slicePx;
      remaining -= sliceMm;
      isFirst = false;
    }

    const totalPages = pdf.getNumberOfPages();
    for (let page = 1; page <= totalPages; page += 1) {
      pdf.setPage(page);
      pdf.setDrawColor(226, 232, 240);
      pdf.line(margin, pageH - footerH, pdfW - margin, pageH - footerH);
      pdf.setTextColor(148, 163, 184);
      pdf.setFontSize(6.5);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`GES Master AI © ${new Date().getFullYear()}`, margin, pageH - footerH + 5);
      pdf.text(`${page} / ${totalPages}`, pdfW - margin, pageH - footerH + 5, { align: 'right' });
    }

    const blob = pdf.output('blob');
    const filename = `ges_ai_raporu_${Date.now()}.pdf`;
    const url = URL.createObjectURL(blob);

    return {
      url,
      filename,
      title: 'GES Master AI Raporu',
      messageId,
      prompt: message?.content,
      vendor: message?.metadata?.vendor,
      model: message?.metadata?.model,
    };
  };

  const handleOpenPdfPreview = async (messageId: string) => {
    const toastId = toast.loading('PDF hazırlanıyor...');
    try {
      const preview = await buildPdfPreview(messageId);
      setPdfPreview((current) => {
        if (current) URL.revokeObjectURL(current.url);
        return preview;
      });
      toast.success('PDF önizleme hazır.', { id: toastId });
    } catch (error) {
      console.error(error);
      toast.error('PDF oluşturulamadı', { id: toastId });
    }
  };

  const queueAttachmentMail = (draft: AttachmentMailDraft) => {
    setAttachmentMailDraft(draft);
  };

  const clearImageAttachment = () => {
    setImageAttachment((current) => {
      if (current?.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(current.previewUrl);
      }
      return null;
    });
  };

  const beginScreenCapture = () => {
    if (isLoading) return;
    setSelectionRect(null);
    setCaptureStartPoint(null);
    setIsCaptureMode(true);
    toast.dismiss();
    toast('Ekranda yorumlatmak istediğin alanı fare ile seç.');
  };

  const cancelCaptureMode = () => {
    setIsCaptureMode(false);
    setCaptureStartPoint(null);
    setSelectionRect(null);
  };

  const performCapture = async (rect: SelectionRect) => {
    const padding = 2;
    const x = Math.max(0, Math.floor(rect.x - padding));
    const y = Math.max(0, Math.floor(rect.y - padding));
    const width = Math.max(1, Math.floor(rect.width + padding * 2));
    const height = Math.max(1, Math.floor(rect.height + padding * 2));

    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));

    let canvas: HTMLCanvasElement;
    try {
      canvas = await html2canvas(document.documentElement, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
        scale: Math.min(window.devicePixelRatio || 1, 2),
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
        ignoreElements: (el) => {
          const tag = el.tagName?.toLowerCase();
          if (tag === 'iframe' || tag === 'video' || tag === 'canvas') return true;
          return false;
        },
      });
    } catch (firstError) {
      const fallbackDataUrl = await toPng(document.documentElement, {
        cacheBust: true,
        backgroundColor: '#ffffff',
        pixelRatio: 1,
        filter: (node: HTMLElement) => {
          const tag = node.tagName?.toLowerCase();
          return tag !== 'iframe' && tag !== 'video' && tag !== 'canvas';
        },
      });
      const fallbackImage = new Image();
      fallbackImage.src = fallbackDataUrl;
      await new Promise<void>((resolve, reject) => {
        fallbackImage.onload = () => resolve();
        fallbackImage.onerror = () => reject(firstError);
      });
      const fallbackCanvas = document.createElement('canvas');
      fallbackCanvas.width = fallbackImage.naturalWidth;
      fallbackCanvas.height = fallbackImage.naturalHeight;
      const fallbackCtx = fallbackCanvas.getContext('2d');
      if (!fallbackCtx) throw firstError;
      fallbackCtx.drawImage(fallbackImage, 0, 0);
      canvas = fallbackCanvas;
    }

    const isFallbackScale = canvas.width !== window.innerWidth && canvas.height !== window.innerHeight;
    const scaleX = isFallbackScale ? canvas.width / Math.max(1, document.documentElement.scrollWidth) : canvas.width / window.innerWidth;
    const scaleY = isFallbackScale ? canvas.height / Math.max(1, document.documentElement.scrollHeight) : canvas.height / window.innerHeight;
    const cropX = Math.floor((isFallbackScale ? window.scrollX + x : x) * scaleX);
    const cropY = Math.floor((isFallbackScale ? window.scrollY + y : y) * scaleY);
    const cropWidth = Math.floor(width * scaleX);
    const cropHeight = Math.floor(height * scaleY);

    const maxWidth = 1600;
    const resizeRatio = cropWidth > maxWidth ? maxWidth / cropWidth : 1;
    const outWidth = Math.max(1, Math.floor(cropWidth * resizeRatio));
    const outHeight = Math.max(1, Math.floor(cropHeight * resizeRatio));

    const output = document.createElement('canvas');
    output.width = outWidth;
    output.height = outHeight;
    const ctx = output.getContext('2d');
    if (!ctx) throw new Error('Görsel hazırlama başarısız oldu.');
    ctx.drawImage(canvas, cropX, cropY, cropWidth, cropHeight, 0, 0, outWidth, outHeight);

    const previewUrl = output.toDataURL('image/jpeg', 0.9);
    const dataBase64 = previewUrl.split(',')[1] || '';
    if (!dataBase64) throw new Error('Görsel kodlanamadı.');

    setImageAttachment((current) => {
      if (current?.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(current.previewUrl);
      }
      return {
        previewUrl,
        dataBase64,
        mimeType: 'image/jpeg',
        width: outWidth,
        height: outHeight,
      };
    });

    if (!input.trim()) {
      setInput('Bu ekran alanını analiz edip bulguları yorumlar mısın?');
    }
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleCaptureMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const start = { x: event.clientX, y: event.clientY };
    setCaptureStartPoint(start);
    setSelectionRect({ x: start.x, y: start.y, width: 0, height: 0 });
  };

  const handleCaptureMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!captureStartPoint) return;
    const currentX = event.clientX;
    const currentY = event.clientY;
    const nextRect: SelectionRect = {
      x: Math.min(captureStartPoint.x, currentX),
      y: Math.min(captureStartPoint.y, currentY),
      width: Math.abs(currentX - captureStartPoint.x),
      height: Math.abs(currentY - captureStartPoint.y),
    };
    setSelectionRect(nextRect);
  };

  const handleCaptureMouseUp = async () => {
    const rect = selectionRect;
    setCaptureStartPoint(null);
    setSelectionRect(null);

    if (!rect || rect.width < 24 || rect.height < 24) {
      setIsCaptureMode(false);
      toast.error('Daha büyük bir alan seçmelisin.');
      return;
    }

    setIsCaptureMode(false);
    setIsCapturingSnapshot(true);
    const toastId = toast.loading('Ekran görüntüsü hazırlanıyor...');
    try {
      await performCapture(rect);
      toast.success('Görsel eklendi. Promptu göndererek yorumlatabilirsin.', { id: toastId });
    } catch (error) {
      console.error(error);
      toast.error('Ekran görüntüsü alınamadı.', { id: toastId });
    } finally {
      setIsCapturingSnapshot(false);
    }
  };

  const handleSend = async (text: string) => {
    if (!text.trim()) return;
    const contentText = imageAttachment ? `${text}\n\n[Ekran görseli eklendi]` : text;
    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: contentText };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    setIsSearchMode(false);
    try {
      const history = messages.map(m => ({ role: m.role, content: m.content })).slice(-10);
      console.log(`[FE Debug] Sending to AI: vendor=${vendor}, prompt=${text.substring(0, 20)}...`);
      const result = await aiApi.ask({
        prompt: text,
        history,
        vendor,
        model: vendor === 'gemini' ? geminiVersion : undefined,
        image: imageAttachment
          ? {
              mimeType: imageAttachment.mimeType,
              dataBase64: imageAttachment.dataBase64,
            }
          : undefined,
      });
      
      const assistantMsg: Message = { 
        id: (result as any).assistantMessage?.id || (Date.now() + 1).toString(), 
        role: 'assistant', 
        content: result.response, 
        metadata: result.metadata,
        timestamp: (result as any).assistantMessage?.timestamp || new Date().toISOString()
      };

      setMessages(prev => {
        const lastUser = prev[prev.length - 1];
        if (lastUser && lastUser.role === 'user' && (result as any).userMessage?.id) {
          lastUser.id = (result as any).userMessage.id;
          lastUser.timestamp = (result as any).userMessage.timestamp;
        }
        return [...prev, assistantMsg];
      });

      // Check for PDF tool call — trigger inline preview after render
      const pdfCall = result.metadata.toolCalls?.find((c: any) => c.name === 'generate_pdf_report');
      if (pdfCall) {
        const msgId = (result as any).assistantMessage?.id || (Date.now() + 1).toString();
        setTimeout(() => handleOpenPdfPreview(msgId), 500);
      }
      if (imageAttachment) {
        clearImageAttachment();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || "Cevap üretilemedi.";
      setMessages(prev => [...prev, { id: Date.now().toString(), role: 'assistant', content: errorMessage }]);
    } finally { setIsLoading(false); }
  };

  const handleDeleteItem = async (messageId: string) => {
    try {
      await aiApi.deleteHistoryItem(messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Mesaj silindi');
    } catch (error) {
      toast.error('Silinemedi');
    }
  };

  if (!isOpen) return null;
  const displayMessages = isSearchMode && searchQuery.trim() ? searchResults : messages;

  return (
    <>
      {!isCaptureMode && !isCapturingSnapshot && (
      <div className={cn("fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm transition-all", isMaximized ? "p-0" : "p-4")}>
        <div className={cn("bg-white flex flex-col overflow-hidden border border-white transition-all duration-500 shadow-2xl", isMaximized ? "w-full h-full rounded-none" : "w-full max-w-5xl h-[85vh] rounded-[32px]")}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-slate-50 grow-0 shrink-0 bg-white/80 backdrop-blur-md">
            <div className="flex items-center gap-3">
              <AIIcon size={14} />
              <div>
                <div className="text-[13px] font-black text-slate-800 tracking-tight">GES Master AI</div>
                <div className="flex items-center gap-1 opacity-60">
                  <Cpu size={9} className="text-blue-500" />
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-none">Akıllı Analiz İstasyonu</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {vendor === 'gemini' && (
                <div className="flex items-center bg-blue-50/50 p-0.5 rounded-lg border border-blue-100 scale-90">
                  <button onClick={() => setGeminiVersion('2.5')} className={cn("px-2 py-1 rounded-md text-[8px] font-black transition-all", geminiVersion === '2.5' ? "bg-white text-blue-600 shadow-sm" : "text-blue-300")}>v2.5</button>
                  <button onClick={() => setGeminiVersion('3')} className={cn("px-2 py-1 rounded-md text-[8px] font-black transition-all", geminiVersion === '3' ? "bg-white text-blue-600 shadow-sm" : "text-blue-300")}>v3</button>
                </div>
              )}

              <div className="flex items-center bg-slate-100 p-0.5 rounded-xl border border-slate-200">
                {availableVendors.includes('gemini') && (
                  <button onClick={() => setVendor('gemini')} className={cn("px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all", vendor === 'gemini' ? "bg-white text-blue-600 shadow-sm" : "text-slate-400")}>Gemini</button>
                )}
                {availableVendors.includes('anthropic') && (
                  <button onClick={() => setVendor('anthropic')} className={cn("px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all", vendor === 'anthropic' ? "bg-white text-orange-600 shadow-sm" : "text-slate-400")}>Claude</button>
                )}
                {availableVendors.includes('deepseek') && (
                  <button onClick={() => setVendor('deepseek')} className={cn("px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all", vendor === 'deepseek' ? "bg-white text-emerald-600 shadow-sm" : "text-slate-400")}>DeepSeek</button>
                )}
              </div>

              <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg">
                <button onClick={() => setIsSearchMode(!isSearchMode)} className={cn("p-1.5 rounded-md transition-all", isSearchMode ? "bg-white text-blue-600 shadow-sm" : "text-slate-400")}><Search size={14} /></button>
                <button onClick={handleClear} className="p-1.5 hover:bg-red-50 hover:text-red-500 rounded-md text-slate-400"><Trash2 size={14} /></button>
              </div>
              <div className="w-[1px] h-4 bg-slate-200 mx-1" />
              <button onClick={() => setIsMaximized(!isMaximized)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400" title={isMaximized ? "Küçült" : "Tam Ekran Yap"}>
                {isMaximized ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400"><X size={16} /></button>
            </div>
          </div>

          {/* Search Header */}
          {isSearchMode && (
            <div className="px-6 py-3 bg-blue-50/30 border-b border-blue-100 animate-in slide-in-from-top-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400" size={12} />
                <input type="text" autoFocus value={searchQuery} onChange={(e) => handleSearch(e.target.value)} placeholder="Mesajlar arasında ara..." className="w-full bg-white border border-blue-100 rounded-xl py-1.5 pl-9 pr-4 text-[11px] outline-none font-bold text-slate-600" />
              </div>
            </div>
          )}

          {/* Messages Area */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-8 space-y-10 custom-scrollbar bg-white">
            {displayMessages.map((msg) => (
              <div key={msg.id} className={cn("flex gap-4 items-start mx-auto group transition-all duration-500", isMaximized ? "max-w-7xl" : "max-w-4xl")}>
                <div className={cn( "w-8 h-8 rounded-xl flex items-center justify-center shrink-0 shadow-sm transition-all duration-300", 
                  msg.role === 'assistant' ? "bg-gradient-to-tr from-blue-600 to-indigo-500 shadow-blue-200" : "bg-slate-100 border border-slate-200" )}>
                  {msg.role === 'assistant' ? <AIIcon size={14} className="text-white" /> : <User size={14} className="text-slate-500" />}
                </div>

                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={cn( "text-[9px] font-black uppercase tracking-[0.2em]", msg.role === 'assistant' ? "text-blue-600" : "text-slate-400" )}>
                        {msg.role === 'assistant' ? 'GES MASTER AI' : 'MÜHENDİS (SİZ)'}
                      </span>
                      {msg.role === 'assistant' && msg.metadata && (
                        <span className="text-[8px] font-bold text-slate-300 uppercase tracking-widest bg-slate-50 px-1.5 py-0.5 rounded-md border border-slate-100">
                          {getModelBadgeLabel(msg.metadata.vendor, msg.metadata.model)}
                        </span>
                      )}
                      {msg.timestamp && (
                        <span className="text-[8px] font-bold text-slate-300 uppercase tracking-wider">
                          {new Date(msg.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>

                    <button 
                      onClick={() => msg.id && handleDeleteItem(msg.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-rose-50 hover:text-rose-500 text-slate-300 rounded-md transition-all"
                      title="Mesajı Sil"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>

                  <div
                    ref={msg.role === 'assistant' ? (el) => { if (el) messageRefs.current.set(msg.id, el); else messageRefs.current.delete(msg.id); } : undefined}
                    className={cn( "text-[13px] leading-relaxed prose prose-slate max-w-none prose-p:leading-relaxed prose-pre:bg-slate-900 prose-pre:rounded-xl",
                    msg.role === 'user' ? "font-bold text-slate-700 bg-slate-50/50 p-4 rounded-2xl border border-dashed border-slate-200" : "text-slate-800" )}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                        table: ({node, ...props}) => {
                          const tableContainerId = `ai-table-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                          const getCsvBlobFromTable = (table: HTMLTableElement) => {
                            let csv = [];
                            const rows = table.querySelectorAll('tr');
                            for (let i = 0; i < rows.length; i++) {
                              const row = [];
                              const cols = rows[i].querySelectorAll('td, th');
                              for (let j = 0; j < cols.length; j++) {
                                row.push('"' + (cols[j] as HTMLElement).innerText.replace(/"/g, '""') + '"');
                              }
                              csv.push(row.join(','));
                            }
                            return new Blob([csv.join('\n')], { type: 'text/csv;charset=utf-8;' });
                          };

                          const getTableElement = () => {
                            const container = document.getElementById(tableContainerId);
                            return container?.querySelector('table') as HTMLTableElement | null;
                          };

                          const downloadCSV = () => {
                            const table = getTableElement();
                            if (!table) return;
                            const csvFile = getCsvBlobFromTable(table);
                            const link = document.createElement('a'); link.href = URL.createObjectURL(csvFile);
                            link.setAttribute('download', `ai_download_${Date.now()}.csv`); link.click();
                          };

                          const sendTableByMail = () => {
                            const table = getTableElement();
                            if (!table) return;
                            const csvFile = getCsvBlobFromTable(table);
                            queueAttachmentMail({
                              title: 'AI Tablo Verisi',
                              filename: `ai_tablo_${Date.now()}.csv`,
                              contentType: 'text/csv',
                              blob: csvFile,
                            });
                          };

                          const tableActionItems: DropdownItem[] = [
                            {
                              label: 'Veriyi CSV İndir',
                              icon: <Database size={14} />,
                              onClick: downloadCSV,
                              variant: 'info',
                            },
                            {
                              label: 'Mail Gönder',
                              icon: <Mail size={14} />,
                              onClick: sendTableByMail,
                              variant: 'warning',
                            },
                          ];

                          return (
                            <div id={tableContainerId} className="group/table relative my-4 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-lg shadow-slate-100">
                              <div className="export-ignore absolute right-3 top-3 z-10 opacity-0 group-hover/table:opacity-100 transition-all">
                                <DropdownMenu title="İşlemler" items={tableActionItems} className="!inline-block" />
                              </div>
                              <div className="overflow-x-auto"><table className="w-full text-[11px] text-left border-collapse" {...props} /></div>
                            </div>
                          );
                        },
                        th: ({node, ...props}) => <th className="bg-slate-50 p-2.5 font-black border-b border-slate-100 text-slate-500 uppercase text-[9px] tracking-widest" {...props} />,
                        td: ({node, ...props}) => <td className="p-2.5 border-b border-slate-50 text-slate-600 font-medium" {...props} />,
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                    {msg.role === 'assistant' && msg.metadata?.chart && (
                      <AIChartCard chart={msg.metadata.chart as AIChartSpec} onQueueAttachmentMail={queueAttachmentMail} />
                    )}
                  </div>

                  {msg.role === 'assistant' && msg.metadata && (
                    <div className="export-ignore space-y-4 pt-1">
                      <div className="flex items-center gap-4">
                        <button onClick={() => handleOpenPdfPreview(msg.id)} className="flex items-center gap-1.5 group/pdf text-rose-500/60 hover:text-rose-600 transition-all bg-rose-50/50 px-2 py-1 rounded-md border border-rose-100/50">
                          <FileText size={12} />
                          <span className="text-[9px] font-black uppercase tracking-tight">PDF ÖNİZLE</span>
                        </button>
                        <button onClick={() => setSelectedMeta(msg.metadata)} className="flex items-center gap-1.5 group/meta text-blue-500/60 hover:text-blue-600 transition-all bg-blue-50/50 px-2 py-1 rounded-md border border-blue-100/50">
                          <Info size={12} />
                          <span className="text-[9px] font-black uppercase tracking-tight">ANALİZ DETAYLARI</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className={cn("flex gap-4 items-start mx-auto transition-all duration-500", isMaximized ? "max-w-7xl" : "max-w-4xl")}>
                <div className="w-8 h-8 rounded-xl bg-blue-600 flex items-center justify-center shrink-0 shadow-lg shadow-blue-200">
                  <Loader2 size={14} className="text-white animate-spin" />
                </div>
                <div className="space-y-2 py-1">
                  <div className="h-1.5 w-10 bg-slate-100 rounded-full animate-pulse" />
                  <div className="h-3 w-48 bg-slate-50 rounded-full animate-pulse" />
                </div>
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="px-6 py-4 bg-white border-t border-slate-50 relative z-10">
            <div className={cn("mx-auto transition-all duration-500", isMaximized ? "max-w-7xl" : "max-w-4xl")}>
              {imageAttachment && (
                <div className="mb-3 flex items-center justify-between gap-3 rounded-2xl border border-violet-200 bg-violet-50/70 px-3 py-2">
                  <div className="flex min-w-0 items-center gap-3">
                    <img src={imageAttachment.previewUrl} alt="Secili ekran alani" className="h-12 w-20 rounded-lg border border-violet-200 object-cover" />
                    <div className="min-w-0">
                      <div className="truncate text-[11px] font-black uppercase tracking-wider text-violet-700">Ekran görseli eklendi</div>
                      <div className="text-[10px] font-semibold text-violet-600">{imageAttachment.width} x {imageAttachment.height} px</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clearImageAttachment}
                    className="rounded-lg border border-violet-200 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-violet-700 hover:bg-violet-100"
                  >
                    Kaldır
                  </button>
                </div>
              )}
              <form onSubmit={(e) => { e.preventDefault(); handleSend(input); }} className={cn("flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-2xl px-4 py-2 transition-all duration-300", "focus-within:border-blue-400 focus-within:bg-white focus-within:shadow-[0_0_20px_-10px_rgba(37,99,235,0.2)]")}>
                <div className="w-8 h-8 rounded-xl bg-white border border-slate-100 flex items-center justify-center shadow-sm shrink-0"><AIIcon size={14} className={cn("transition-all", isLoading ? "animate-pulse text-blue-600" : "text-slate-400")} /></div>
                <input ref={inputRef} type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder={isSearchMode ? "Aramayı kapatıp mesaj yazmak için X butonuna basın..." : "Analitik bir soru sorun..."} className="bg-transparent border-none outline-none text-[13px] font-bold text-slate-700 w-full placeholder:text-slate-300 px-2" />
                <button type="submit" disabled={!input.trim() || isLoading} className={cn("group p-2.5 transition-all rounded-xl flex items-center justify-center overflow-hidden relative", input.trim() ? "bg-blue-600 text-white shadow-lg shadow-blue-600/10" : "bg-slate-100 text-slate-300 opacity-50")}>
                  <div className="relative z-10">{isLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={2.5} className="group-hover:rotate-12 transition-transform" />}</div>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      )}

      {isCaptureMode && !isCapturingSnapshot && (
        <div
          className="fixed inset-0 z-[140] cursor-crosshair"
          onMouseDown={handleCaptureMouseDown}
          onMouseMove={handleCaptureMouseMove}
          onMouseUp={() => {
            void handleCaptureMouseUp();
          }}
        >
          <div className="pointer-events-none absolute inset-0 bg-black/20" />
          {selectionRect && (
            <div
              className="pointer-events-none absolute border-2 border-violet-500 bg-violet-200/20 shadow-[0_0_0_9999px_rgba(0,0,0,0.15)]"
              style={{
                left: `${selectionRect.x}px`,
                top: `${selectionRect.y}px`,
                width: `${selectionRect.width}px`,
                height: `${selectionRect.height}px`,
              }}
            />
          )}
          <div className="pointer-events-none absolute left-1/2 top-5 -translate-x-1/2 rounded-xl border border-violet-200 bg-white/95 px-3 py-2 text-[11px] font-bold text-violet-700 shadow-lg">
            Alan seçimi: sürükleyip bırak. İptal için ESC.
          </div>
          <button
            type="button"
            className="absolute right-5 top-5 rounded-lg border border-white/50 bg-black/40 px-3 py-1.5 text-[11px] font-semibold text-white"
            onClick={(event) => {
              event.stopPropagation();
              cancelCaptureMode();
            }}
          >
            İptal
          </button>
        </div>
      )}

      {/* Analytics Modal */}
      {selectedMeta && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-slate-900 w-full max-w-4xl h-[85vh] flex flex-col rounded-[32px] overflow-hidden border border-slate-800 shadow-[0_0_80px_rgba(0,0,0,0.5)]">
            <div className="flex items-center justify-between px-8 py-5 border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm grow-0 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-blue-500/20 flex items-center justify-center"><Database className="text-blue-400" size={18} /></div>
                <div>
                  <h3 className="text-white font-black text-lg leading-tight">Teknik Analiz Paneli</h3>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">Response Metadata & Performance</div>
                </div>
              </div>
              <button onClick={() => setSelectedMeta(null)} className="p-2 hover:bg-slate-800 rounded-2xl text-slate-500 hover:text-white transition-all"><X size={24} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar bg-slate-900/30">
              {getAnalysisStats(selectedMeta).length > 0 && (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
                  {getAnalysisStats(selectedMeta).map((item) => (
                    <div key={item.label} className="rounded-3xl border border-slate-700/50 bg-slate-800/50 p-5 backdrop-blur-sm">
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{item.label}</div>
                      <div className="mt-2 text-2xl font-black text-white">
                        {formatStatValue(item.value)}
                        {item.suffix ? <span className="ml-1 text-sm font-bold text-slate-500">{item.suffix}</span> : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 border border-slate-700/50 p-6 rounded-3xl backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-2 text-[10px] font-black uppercase text-slate-400 tracking-wider"><Cpu size={14} className="text-blue-400" /> Model Gecikmesi</div>
                  <div className="font-mono text-3xl font-black text-emerald-400">{(selectedMeta.latencyMs / 1000).toFixed(2)}<span className="text-sm ml-1 text-emerald-400/50 italic font-normal">saniye</span></div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700/50 p-6 rounded-3xl backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-2 text-[10px] font-black uppercase text-slate-400 tracking-wider"><History size={14} className="text-orange-400" /> Sağlayıcı & Sürüm</div>
                  <div className="text-xl font-black text-white capitalize">{selectedMeta.vendor} <span className="text-sm font-bold text-slate-500">({selectedMeta.model})</span></div>
                </div>
              </div>
              {selectedMeta.context && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div className="bg-slate-800/50 border border-slate-700/50 p-6 rounded-3xl backdrop-blur-sm">
                    <div className="text-[10px] font-black uppercase text-slate-400 tracking-wider mb-3">Context Özeti</div>
                    <div className="space-y-2 text-sm text-slate-200">
                      <div><span className="text-slate-500">Vendor:</span> {VENDOR_LABELS[selectedMeta.context.vendor as keyof typeof VENDOR_LABELS] || selectedMeta.context.vendor}</div>
                      <div><span className="text-slate-500">Model:</span> {selectedMeta.context.model}</div>
                      <div><span className="text-slate-500">History:</span> {selectedMeta.context.historyUsed?.length || 0} mesaj</div>
                      <div><span className="text-slate-500">Memory:</span> {selectedMeta.context.memoriesUsed?.length || 0} kayıt</div>
                      <div><span className="text-slate-500">SQL:</span> {selectedMeta.context.sqlQueries?.length || 0} sorgu</div>
                      <div><span className="text-slate-500">Tool:</span> {selectedMeta.context.toolCalls?.length || 0} çağrı</div>
                    </div>
                  </div>
                  <div className="bg-slate-800/50 border border-slate-700/50 p-6 rounded-3xl backdrop-blur-sm">
                    <div className="text-[10px] font-black uppercase text-slate-400 tracking-wider mb-3">Hafıza Kayıtları</div>
                    <div className="max-h-56 overflow-y-auto custom-scrollbar space-y-2 text-sm text-slate-200">
                      {selectedMeta.context.memoriesUsed?.length ? selectedMeta.context.memoriesUsed.map((memory: string, index: number) => (
                        <div key={`${memory}-${index}`} className="rounded-2xl bg-slate-950/60 px-4 py-3 text-slate-300">{memory}</div>
                      )) : <div className="rounded-2xl bg-slate-950/60 px-4 py-3 text-slate-500">Bu yanıtta ek hafıza kaydı kullanılmadı.</div>}
                    </div>
                  </div>
                </div>
              )}
              {selectedMeta.context && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Gönderilen History</div>
                    <pre className="bg-black/60 p-6 rounded-[28px] border border-slate-800 text-xs text-slate-200 overflow-auto font-mono leading-relaxed">{JSON.stringify(selectedMeta.context.historyUsed || [], null, 2)}</pre>
                  </div>
                  <div className="space-y-4">
                    <div className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Çalışan SQL / Tool Context</div>
                    <pre className="bg-black/60 p-6 rounded-[28px] border border-slate-800 text-xs text-emerald-300 overflow-auto font-mono leading-relaxed">{JSON.stringify({ sqlQueries: selectedMeta.context.sqlQueries || [], toolCalls: selectedMeta.context.toolCalls || [] }, null, 2)}</pre>
                  </div>
                </div>
              )}
              {selectedMeta.context && (
                <div className="space-y-4">
                  <div className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Effective System Instruction</div>
                  <pre className="bg-black/60 p-8 rounded-[32px] border border-slate-800 text-xs text-violet-300 overflow-auto font-mono leading-relaxed whitespace-pre-wrap">{selectedMeta.context.systemInstruction}</pre>
                </div>
              )}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Ham Veri (Raw Data)</div>
                  <div className="text-[10px] font-bold text-slate-600 bg-slate-950 px-3 py-1 rounded-full uppercase">Application/JSON</div>
                </div>
                <div className="relative group/json">
                  <div className="absolute inset-0 bg-blue-500/5 rounded-[24px] blur-xl opacity-0 group-hover/json:opacity-100 transition-opacity" />
                  <pre className="relative bg-black/60 p-8 rounded-[32px] border border-slate-800 text-xs text-blue-300 overflow-x-auto overflow-y-visible font-mono leading-relaxed selection:bg-blue-500/30">{JSON.stringify(selectedMeta, null, 2)}</pre>
                </div>
              </div>
            </div>
            <div className="px-8 py-4 border-t border-slate-800 bg-slate-950/30 text-center grow-0 shrink-0">
              <span className="text-[9px] font-bold text-slate-600 uppercase tracking-widest italic">Antigravity AI Engineering Tools © 2026</span>
            </div>
          </div>
        </div>
      )}

      {pdfPreview && (
        <AIPdfModal
          preview={pdfPreview}
          users={users}
          currentUserEmail={user?.email}
          onClose={() => {
            URL.revokeObjectURL(pdfPreview.url);
            setPdfPreview(null);
          }}
        />
      )}

      {attachmentMailDraft && (
        <AIAttachmentMailModal
          draft={attachmentMailDraft}
          users={users}
          currentUserEmail={user?.email}
          onClose={() => setAttachmentMailDraft(null)}
        />
      )}
    </>
  );
}
